import React, { useEffect, useState } from 'react';
import { TeamMember } from '../data/team';
import { X, Linkedin, Instagram, Facebook, Github, Globe } from 'lucide-react';
import { FaXTwitter } from 'react-icons/fa6';

export const TeamMemberModal: React.FC<{ member: TeamMember; onClose: () => void }> = ({ member, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Matches the animation duration of 0.3s
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={`modal-title-${member.id}`}
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
    >
      <div
        className={`fixed inset-0 bg-black/60 transition-opacity ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />

      <div
        className={`relative bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-border ${
          isClosing ? 'animate-scale-out' : 'animate-scale-in'
        }`}
      >
        <div className='flex items-start justify-between p-6 border-b border-border'>
          <div>
            <h2 id={`modal-title-${member.id}`} className='text-2xl font-bold text-foreground font-heading'>
              {member.name}
            </h2>
            <p className='text-primary font-semibold'>{member.title}</p>
          </div>
          <button
            onClick={handleClose}
            aria-label='Close'
            className='text-muted-foreground hover:text-foreground transition-colors'
          >
            <X className='h-6 w-6' />
          </button>
        </div>

        <div className='p-6 overflow-y-auto'>
          <p className='text-muted-foreground whitespace-pre-wrap leading-relaxed'>{member.bio}</p>
        </div>

        <div className='flex flex-wrap justify-end gap-2 p-4 border-t border-border bg-muted/50 rounded-b-lg'>
          {Object.entries(member.social).map(([platform, url]) => {
            if (!url) return null;
            const Icon = {
              linkedin: Linkedin,
              x: FaXTwitter,
              instagram: Instagram,
              facebook: Facebook,
              github: Github,
              website: Globe,
            }[platform];

            return (
              <a
                key={platform}
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center justify-center bg-secondary text-secondary-foreground p-2 rounded-md hover:bg-secondary/80 transition-colors'
                aria-label={`Visit ${member.name}'s ${platform}`}
              >
                {Icon && <Icon className='w-5 h-5' />}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};
